/**
 * Centralized analytics tracking utility.
 *
 * Pushes events to Google Tag Manager's dataLayer, Microsoft Clarity, and
 * (if present) Meta Pixel. Events are silently dropped if analytics scripts
 * are not loaded, for example before consent is granted.
 *
 * Selected high-signal events are ALSO forwarded to our own first-party
 * analytics backend (see ./analyticsClient) so the admin Site Analytics page
 * has an owned source of truth alongside Clarity/GTM.
 */
import { queueEvent, EVENTS } from "./analyticsClient";
import { readConsent } from "./consent";

// Google Ads click identifiers + UTM parameters we persist for attribution.
const ATTRIBUTION_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

export const ATTRIBUTION_STORAGE_KEY = "pp_attribution_v1";
const MAX_AGE = 30 * 24 * 60 * 60 * 1000;
const CLICK_KEYS = new Set(["gclid", "gbraid", "wbraid"]);
const allowedAttribution = (values, consent) => Object.fromEntries(
  ATTRIBUTION_KEYS.filter((key) => (CLICK_KEYS.has(key) ? consent.marketing : consent.analytics) &&
    typeof values?.[key] === "string" && values[key].length > 0)
    .map((key) => [key, values[key].slice(0, 256)])
);

export function reconcileAttributionConsent(consent = readConsent()) {
  try {
    ATTRIBUTION_KEYS.forEach((key) => localStorage.removeItem(`attr_${key}`));
    if (!consent.analytics && !consent.marketing) {
      localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return {};
    }
    const record = JSON.parse(localStorage.getItem(ATTRIBUTION_STORAGE_KEY) || "null");
    if (!record || record.version !== 1 || !Number.isFinite(record.captured_at) ||
        record.captured_at > Date.now() || Date.now() - record.captured_at >= MAX_AGE) {
      localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return {};
    }
    const values = allowedAttribution(record.values, consent);
    if (Object.keys(values).length === 0) localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
    else if (JSON.stringify(values) !== JSON.stringify(record.values)) {
      localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify({ ...record, values }));
    }
    return values;
  } catch {
    try { localStorage.removeItem(ATTRIBUTION_STORAGE_KEY); } catch { /* storage blocked */ }
    return {};
  }
}

/** One campaign record, purpose-limited and expiring after 30 days. */
export const captureAttribution = () => {
  try {
    const consent = readConsent();
    const previous = reconcileAttributionConsent(consent);
    if (!consent.analytics && !consent.marketing) return;
    const params = new URLSearchParams(window.location.search);
    if (!ATTRIBUTION_KEYS.some((key) => params.has(key))) return;
    const values = allowedAttribution(Object.fromEntries(params), consent);
    if (!Object.keys(values).length) { localStorage.removeItem(ATTRIBUTION_STORAGE_KEY); return; }
    if (JSON.stringify(previous) === JSON.stringify(values)) return;
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify({
      version: 1, captured_at: Date.now(), values,
    }));
  } catch { /* storage is optional */ }
};

export const getAttribution = () => reconcileAttributionConsent();

/**
 * Set Microsoft Clarity custom tags when Clarity is available.
 */
export const setClarityTags = (tags = {}) => {
  try {
    if (!readConsent().analytics || typeof window === "undefined" || typeof window.clarity !== "function") {
      return;
    }

    Object.entries(tags).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      window.clarity("set", key, String(value));
    });
  } catch {
    // Analytics should never break the app.
  }
};

/**
 * Track a Microsoft Clarity custom event when Clarity is available.
 */
export const trackClarityEvent = (eventName) => {
  try {
    if (readConsent().analytics && typeof window !== "undefined" && typeof window.clarity === "function") {
      window.clarity("event", eventName);
    }
  } catch {
    // Silently fail.
  }
};

/**
 * Push an event to GTM dataLayer and Clarity.
 */
export const trackEvent = (eventName, params = {}) => {
  try {
    if (!readConsent().analytics) return;
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...params,
      });
    }
    setClarityTags(params);
    trackClarityEvent(eventName);
  } catch {
    // Analytics should never break the app.
  }
};

/**
 * Track a Meta Pixel custom event.
 */
export const trackMetaEvent = (eventName, params = {}) => {
  try {
    if (readConsent().marketing && typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("trackCustom", eventName, params);
    }
  } catch {
    // Silently fail.
  }
};

export const trackGoogleAdsConversion = (params = {}) => {
  try {
    const sendTo = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_SEND_TO;
    if (
      readConsent().marketing &&
      typeof window !== "undefined" &&
      typeof window.gtag === "function" &&
      sendTo
    ) {
      window.gtag("event", "conversion", {
        send_to: sendTo,
        ...params,
      });
    }
  } catch {
    // Silently fail.
  }
};

// ============================================================================
// PRE-DEFINED EVENTS - Use these throughout the app for consistency
// ============================================================================

/** Standard SEO landing page view */
export const trackSeoLandingViewed = (params = {}) => {
  trackEvent("seo_landing_viewed", params);
};

/** User starts an estimate from a hero/search-style input */
export const trackHeroEstimateStarted = (params = {}) => {
  trackEvent("hero_estimate_started", params);
  trackMetaEvent("HeroEstimateStarted", params);
};

/** User starts a chat conversation */
export const trackChatStarted = (projectType) => {
  trackEvent("chat_started", { project_type: projectType || "unknown" });
  trackMetaEvent("ChatStarted", { project_type: projectType });
  queueEvent(EVENTS.CHAT_OPENED, { project_type: projectType || "unknown" });
};

/** User completes a quote (AI generates estimate) */
export const trackQuoteCompleted = (projectType, estimateLow, estimateHigh) => {
  trackEvent("quote_completed", {
    audience: "homeowner",
    project_type: projectType,
    estimate_low: estimateLow,
    estimate_high: estimateHigh,
  });
  trackMetaEvent("QuoteCompleted", { project_type: projectType });
};

/** User picks a contractor on the project summary — the step between seeing
 * the list and sending a request, where the funnel previously went blind. */
export const trackContractorSelected = (contractorId, projectType) => {
  trackEvent("contractor_selected", {
    audience: "homeowner",
    contractor_id: contractorId,
    project_type: projectType || "unknown",
  });
  queueEvent(EVENTS.CONTRACTOR_SELECTED, {
    contractor_id: contractorId,
    project_type: projectType || "unknown",
  });
};

/** User submits a quote request to contractors */
export const trackQuoteRequestSent = (projectType, contractorCount) => {
  const attribution = getAttribution();
  trackEvent("quote_request_sent", {
    audience: "homeowner",
    project_type: projectType,
    contractor_count: contractorCount,
    ...attribution,
  });
  // Match the "Request quote" conversion action's event snippet (£1 default
  // until real lead values are wired in).
  trackGoogleAdsConversion({ value: 1.0, currency: "GBP" });
  trackMetaEvent("QuoteRequestSent", { project_type: projectType });
};

/** User views a cost guide */
export const trackCostGuideViewed = (guideSlug) => {
  trackEvent("cost_guide_viewed", {
    audience: "homeowner",
    landing_type: "cost_guide",
    guide_slug: guideSlug,
  });
  queueEvent(EVENTS.COST_GUIDE_VIEWED, { slug: guideSlug });
};

/** User views a cost landing page */
export const trackCostLandingViewed = (pageSlug) => {
  trackEvent("cost_landing_viewed", {
    audience: "homeowner",
    landing_type: "cost_answer",
    guide_slug: pageSlug,
    page_slug: pageSlug,
  });
  queueEvent(EVENTS.COST_LANDING_VIEWED, { slug: pageSlug });
};

/** User views a location page */
export const trackLocationPageViewed = (trade, city) => {
  trackEvent("location_page_viewed", {
    audience: "homeowner",
    landing_type: "trade_location",
    trade,
    city,
  });
};

/** User clicks CTA to go to chat from a content page */
export const trackCTAClicked = (source, projectType) => {
  trackEvent("cta_clicked", { source, project_type: projectType });
  trackMetaEvent("CTAClicked", { source });
  queueEvent(EVENTS.CTA_CLICKED, { source, project_type: projectType });
};

/** User signs up */
export const trackSignup = (userType) => {
  trackEvent("signup_completed", { user_type: userType });
  trackMetaEvent("SignupCompleted", { user_type: userType });
};

/** Save project prompt shown */
export const trackSaveProjectPromptShown = (params = {}) => {
  trackEvent("save_project_prompt_shown", params);
};

/** Contractor package/signup interest starts */
export const trackContractorSignupStarted = (params = {}) => {
  trackEvent("contractor_signup_started", {
    audience: "contractor",
    ...params,
  });
  trackMetaEvent("ContractorSignupStarted", params);
  queueEvent(EVENTS.SIGNUP_STARTED, { audience: "contractor" });
};

/** Contractor completes profile setup */
export const trackContractorProfileCompleted = (params = {}) => {
  trackEvent("contractor_profile_completed", {
    audience: "contractor",
    ...params,
  });
};

/** Contractor clicks the phone signup CTA */
export const trackCallSignupClicked = (params = {}) => {
  trackEvent("call_signup_clicked", {
    audience: "contractor",
    ...params,
  });
};

/** User uses the cost calculator */
export const trackCalculatorUsed = (projectType, region) => {
  trackEvent("calculator_used", { project_type: projectType, region });
};

/** User uses the Warm Homes Plan checker */
export const trackGrantCheckerCompleted = (grantCount) => {
  trackEvent("grant_checker_completed", { eligible_grants: grantCount });
};

/** User copies referral link */
export const trackReferralShared = () => {
  trackEvent("referral_shared");
};

/** Contractor copies their refer-a-friend link */
export const trackContractorReferralShared = () => {
  trackEvent("contractor_referral_shared");
};

/** Contractor views quote inbox */
export const trackQuoteInboxViewed = () => {
  trackEvent("quote_inbox_viewed");
};

/** User views contractor profile */
export const trackContractorProfileViewed = (contractorId) => {
  trackEvent("contractor_profile_viewed", { contractor_id: contractorId });
};
